import React, { useCallback, useId } from "react"

import { cn, Textarea, Tooltip } from "@coval/ui"
import { Info } from "lucide-react"

import { JsonEditorAutoSizeProvider } from "@/components/JsonCodeEditor"
import { ReadOnlyCopyButton } from "@/components/version-history/ReadOnlyCopyButton"
import {
  ReadOnlyFieldset,
  useIsInReadOnlyRegion,
} from "@/components/version-history/ReadOnlyRegion"
import type { FormData } from "@/types/FormData"

import { AttributesInput } from "./AttributesInput"

interface AgentDetailsFormProps {
  formData: FormData
  onFormDataChange: (data: FormData) => void
}

export const AgentDetailsForm: React.FC<AgentDetailsFormProps> = ({
  formData,
  onFormDataChange,
}) => {
  const evaluationContextHelpId = useId()
  const isInReadOnlyRegion = useIsInReadOnlyRegion()
  const evaluationContext = formData.prompt || ""
  // A read-only region disables the textarea, which blocks text selection,
  // so it offers a copy control instead.
  const showEvaluationContextCopy =
    isInReadOnlyRegion && evaluationContext.trim().length > 0
  const handlePromptChange = useCallback(
    (value: string) => {
      onFormDataChange({ ...formData, prompt: value })
    },
    [formData, onFormDataChange],
  )

  const handleAttributesChange = useCallback(
    (attributes: Record<string, unknown>) => {
      onFormDataChange({ ...formData, attributes })
    },
    [formData, onFormDataChange],
  )

  return (
    <ReadOnlyFieldset className="space-y-8 w-full">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-coval-text">
            Agent Evaluation Context
          </h4>
          <Tooltip
            content="Enter details about your agent's purpose, guidelines, and scope to help Coval tailor test and evaluation automation for your use case."
            className="max-w-xs whitespace-normal"
          >
            <button type="button" aria-label="Agent evaluation context info">
              <Info className="h-4 w-4 text-coval-text-muted" />
            </button>
          </Tooltip>
        </div>
        <div className="relative">
          <Textarea
            aria-label="Agent evaluation context"
            aria-describedby={evaluationContextHelpId}
            value={evaluationContext}
            onChange={(e) => handlePromptChange(e.target.value)}
            autoComplete="off"
            rows={8}
            className={cn(
              "resize-none overflow-y-auto",
              showEvaluationContextCopy && "pr-11",
            )}
          />
          {showEvaluationContextCopy ? (
            <ReadOnlyCopyButton
              value={evaluationContext}
              label="Copy evaluation context"
              className="absolute right-2 top-2"
            />
          ) : null}
        </div>
        <p
          id={evaluationContextHelpId}
          className="text-sm text-coval-text-muted"
        >
          Used to create tests and metrics; this does not change your agent's
          output.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-coval-text">
            Attributes{" "}
            <span className="text-coval-text-muted font-normal">
              - Structured Context Metadata
            </span>
          </h4>
          <Tooltip
            content="Key-value data about your agent. Reference a value as {{agent.key}} in test cases, expected behaviors, and metrics. A test case that references an attribute passes its value into the simulator's prompt."
            className="max-w-xs whitespace-normal"
          >
            <a
              href="https://docs.coval.ai/concepts/agents/overview#attributes"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Learn more about attributes"
            >
              <Info className="h-4 w-4 text-coval-text-muted" />
            </a>
          </Tooltip>
        </div>
        <JsonEditorAutoSizeProvider maxLines={10}>
          <AttributesInput
            attributes={formData.attributes}
            onChange={handleAttributesChange}
          />
        </JsonEditorAutoSizeProvider>
      </div>
    </ReadOnlyFieldset>
  )
}
